import React, { FC, memo, useMemo } from "react";
import { EntityError } from "@deliverr/replenishment-client";
import { Notification } from "common/components/ui/Notification";
import { isEmpty } from "lodash";
import { MessageDescriptor, useIntl } from "react-intl";
import styled from "@emotion/styled";
import { ThemeProps } from "common/components/ui";

interface ReplenishmentErrorDisplayProps {
  errors: EntityError[];
  errorMessages: Record<string, MessageDescriptor>;
  getDynamicValues?: (error: EntityError) => Record<string, any>;
  containerStyle?: React.CSSProperties;
}

export const ReplenishmentErrorDisplay: FC<ReplenishmentErrorDisplayProps> = memo(
  ({ errors, errorMessages, getDynamicValues = (error) => ({ entityId: error.entityId }), containerStyle }) => {
    const { formatMessage } = useIntl();

    const renderedErrors = useMemo(() => {
      if (isEmpty(errors)) {
        return null;
      }

      const renderError = (error: EntityError, index: number) => {
        const errorMessageTemplate = errorMessages[error.code];
        const dynamicValues = getDynamicValues(error);

        const errorMessage = errorMessageTemplate ? formatMessage(errorMessageTemplate, dynamicValues) : error.message;

        return (
          <NotificationMessage key={index} appearance="DANGER">
            {errorMessage}
          </NotificationMessage>
        );
      };

      return errors.map((error, index) => {
        if (error.errors) {
          return (
            <div key={index}>
              <NotificationMessage appearance="DANGER">
                {formatMessage(errorMessages[error.code], getDynamicValues(error))}
              </NotificationMessage>
              {error.errors.map((nestedError, nestedIndex) => renderError(nestedError, nestedIndex))}
            </div>
          );
        }

        return renderError(error, index);
      });
    }, [errors, errorMessages, getDynamicValues, formatMessage]);

    if (!renderedErrors) {
      return null;
    }

    return <ErrorContainer style={containerStyle}>{renderedErrors}</ErrorContainer>;
  }
);

const ErrorContainer = styled.div`
  max-height: 20rem;
  overflow: auto;
`;

export const NotificationMessage = styled(Notification)<ThemeProps>(
  ({ theme }) => `
    margin-top: ${theme.spacing.S2};
`
);
